import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import { X } from 'lucide-react'
import { KwOverlayScroll } from '../../atoms/KwOverlayScroll'
import { ChipPopoverSelect } from '../../components/ChipPopoverSelect'
import './core-components.css'
import { color, designTokens } from '../../tokens'

export function CoreSwitch({ label, checked, disabled, onChange }: { label: string; checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }) {
  return <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: designTokens['semantic.space.stack'], minHeight: 44, fontSize: 13, color: color.white }}>
    <span>{label}</span><button type="button" role="switch" aria-label={label} aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)} style={{ width: 44, height: 44, flexShrink: 0, padding: 0, border: 0, background: 'transparent', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1 }}>
      <span style={{ display: 'flex', alignItems: 'center', width: 40, height: 24, padding: 4, boxSizing: 'border-box', borderRadius: designTokens['semantic.control.radius-pill'], background: checked ? color.violet : color.darkBorder }}><span style={{ display: 'block', width: 16, height: 16, borderRadius: '50%', background: color.white, transform: checked ? 'translateX(100%)' : undefined }} /></span>
    </button>
  </label>
}

export type CoreButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'link'
  busy?: boolean
  busyLabel?: ReactNode
}

export function CoreButton({ variant = 'secondary', busy = false, busyLabel = 'Lädt…', children, disabled, className, ...props }: CoreButtonProps) {
  return <button {...props} className={`kw-core-button kw-core-button--${variant} ${className ?? ''}`.trim()} disabled={disabled || busy} aria-busy={busy || undefined}>{busy ? busyLabel : children}</button>
}

export type CoreIconButtonProps = Omit<CoreButtonProps, 'children' | 'busyLabel'> & {
  'aria-label': string
  icon: ReactNode
}

export function CoreIconButton({ icon, ...props }: CoreIconButtonProps) {
  return <CoreButton {...props} className={`kw-core-icon-button ${props.className ?? ''}`.trim()}>{icon}</CoreButton>
}

export type CoreInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string
  hint?: string
  error?: string
  /** Keep existing validation-row geometry unless a compact form opts out. */
  reserveMessageSpace?: boolean
}

export function CoreInput({ label, hint, error, id, reserveMessageSpace = true, ...props }: CoreInputProps) {
  const [pointerFocus,setPointerFocus]=useState(false)
  const controlId = id ?? `kw-core-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  const messageId = `${controlId}-message`
  return <label className="kw-core-field" htmlFor={controlId} onPointerDown={()=>setPointerFocus(true)}><span>{label}</span><input {...props} id={controlId} data-pointer-focus={pointerFocus||undefined} onBlur={event=>{setPointerFocus(false);props.onBlur?.(event)}} aria-invalid={error ? true : undefined} aria-describedby={hint || error ? messageId : undefined} />{(reserveMessageSpace || hint || error) && <small id={messageId} className={error ? 'kw-core-error' : undefined}>{error ?? hint}</small>}</label>
}

export type CoreChipProps = ButtonHTMLAttributes<HTMLButtonElement> & { selected?: boolean }

export type CoreTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string
  hint?: string
  error?: string
}

/** Multiline counterpart of CoreInput, using the same field tokens and focus contract. */
export function CoreTextarea({label,hint,error,id,rows=6,...props}:CoreTextareaProps) {
  const generatedId=useId()
  const controlId=id??`kw-core-textarea-${generatedId}`
  const messageId=`${controlId}-message`
  return <label className="kw-core-field" htmlFor={controlId}><span>{label}</span><textarea {...props} aria-label={props['aria-label']??label} id={controlId} rows={rows} aria-invalid={error?true:undefined} aria-describedby={hint||error?messageId:undefined}/>{(hint||error)&&<small id={messageId} className={error?'kw-core-error':undefined}>{error??hint}</small>}</label>
}

export function CoreChip({ selected = false, children, ...props }: CoreChipProps) {
  return <button {...props} type={props.type ?? 'button'} className={`kw-core-chip ${props.className ?? ''}`.trim()} aria-pressed={selected}>{children}</button>
}

export type CoreSelectProps = SelectHTMLAttributes<HTMLSelectElement> & { label: string; options: ReadonlyArray<{ label: string; value: string }> }

export function CoreSelect({ label, options, id, ...props }: CoreSelectProps) {
  const generatedId=useId()
  const controlId=id??`kw-core-select-${generatedId}`
  // The existing multi-select list is a separate interaction; retain its contract.
  if(props.multiple || (props.size??0)>1)return <label className="kw-core-field" htmlFor={controlId}><span>{label}</span><select {...props} id={controlId}>{options.map(option=><option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
  return <CoreSingleSelect {...props} id={controlId} label={label} options={options}/>
}

function CoreSingleSelect({label,options,id,value,defaultValue,onChange,...props}:CoreSelectProps){
  const native=useRef<HTMLSelectElement>(null)
  const [local,setLocal]=useState(String(defaultValue??options[0]?.value??''))
  const selected=String(value??local)
  useEffect(()=>{
    const form=native.current?.form
    const reset=()=>setLocal(String(defaultValue??options[0]?.value??''))
    form?.addEventListener('reset',reset)
    return()=>form?.removeEventListener('reset',reset)
  },[defaultValue,options])
  return <div className="kw-core-field"><label htmlFor={id}>{label}</label>
    <select {...props} ref={native} id={undefined} hidden aria-hidden="true" aria-label={undefined} tabIndex={-1} value={selected} onInvalid={event=>{event.preventDefault();document.getElementById(id!)?.focus();props.onInvalid?.(event)}} onChange={event=>{setLocal(event.target.value);onChange?.(event)}}>{options.map(option=><option key={option.value} value={option.value}>{option.label}</option>)}</select>
    <ChipPopoverSelect appearance="field" fullWidth value={selected} disabled={props.disabled} options={[...options]} ariaLabel={props['aria-label']??label} triggerProps={{id,autoFocus:props.autoFocus,tabIndex:props.tabIndex,'aria-describedby':props['aria-describedby'],'aria-invalid':props['aria-invalid'],'aria-required':props.required}} pillStyle={()=>({})} onChange={next=>{if(!native.current)return;native.current.value=next;native.current.dispatchEvent(new Event('change',{bubbles:true}))}}/>
  </div>
}

export type CoreDialogSheetProps = {
  open: boolean
  title: string
  children: ReactNode
  footer?: ReactNode
  onClose: () => void
  className?: string
  showClose?: boolean
  scrollBody?: boolean
  initialFocus?: 'first-control' | 'dialog'
}

export function CoreDialogSheet({ open, title, children, footer, onClose, className, showClose = true, scrollBody = true, initialFocus = 'first-control' }: CoreDialogSheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) {
      const previousFocus = document.activeElement
      dialog.showModal()
      if (initialFocus === 'dialog') dialog.focus({ preventScroll: true })
      return () => {
        if (dialog.open) dialog.close()
        queueMicrotask(() => {
          if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true })
        })
      }
    }
    if (!open && dialog.open) dialog.close()
  }, [initialFocus, open])
  return <dialog ref={dialogRef} tabIndex={initialFocus === 'dialog' ? -1 : undefined} className={`kw-core-dialog ${className ?? ''}`.trim()} aria-labelledby={titleId} onCancel={(event) => { event.preventDefault(); onClose() }} onClose={onClose} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}><header><h2 id={titleId}>{title}</h2>{showClose ? <CoreIconButton className="kw-core-dialog__close" aria-label="Dialog schließen" icon={<X size={20} strokeWidth={2} aria-hidden />} onClick={onClose} /> : null}</header>{scrollBody ? <KwOverlayScroll sizing="content" className="kw-core-dialog__body">{children}</KwOverlayScroll> : <div className="kw-core-dialog__body">{children}</div>}{footer ? <footer>{footer}</footer> : null}</dialog>
}

export type CoreDecisionDialogProps = {
  open: boolean
  title: string
  description: ReactNode
  primaryLabel: ReactNode
  primaryBusyLabel?: ReactNode
  destructiveLabel: ReactNode
  busy?: boolean
  presentation?: 'auto' | 'mobile' | 'desktop'
  onPrimary: () => void
  onDestructive: () => void
  onClose: () => void
  className?: string
}

export function CoreDecisionDialog({ open, title, description, primaryLabel, primaryBusyLabel, destructiveLabel, busy = false, presentation = 'auto', onPrimary, onDestructive, onClose, className }: CoreDecisionDialogProps) {
  return <CoreDialogSheet
    open={open}
    title={title}
    onClose={onClose}
    initialFocus="dialog"
    className={`kw-core-decision-dialog kw-core-decision-dialog--${presentation} ${className ?? ''}`.trim()}
    footer={<>
      <CoreButton variant="primary" busy={busy} busyLabel={primaryBusyLabel} onClick={onPrimary}>{primaryLabel}</CoreButton>
      <CoreButton className="kw-core-decision-dialog__destructive" disabled={busy} onClick={onDestructive}>{destructiveLabel}</CoreButton>
    </>}
  ><p>{description}</p></CoreDialogSheet>
}
